import React, { useState } from 'react';
import { Search, X, Loader2, User, Clock, FileText } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './EvolutionSearchModal.css';

const EvolutionSearchModal = ({ isOpen, onClose, onSelectPatient }) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      // 1. Fetch matching evolutions
      const { data: evolutionsData, error: evolutionsError } = await supabase
        .from('medical_evolutions')
        .select('*')
        .ilike('content', `%${query}%`)
        .order('created_at', { ascending: false });

      if (evolutionsError) throw evolutionsError;

      if (evolutionsData && evolutionsData.length > 0) {
        // 2. Extract unique patient IDs
        const patientIds = [...new Set(evolutionsData.map(ev => ev.patient_id))].filter(Boolean);

        if (patientIds.length > 0) {
          // 3. Fetch patient profiles
          const { data: patientsData, error: patientsError } = await supabase
            .from('patients')
            .select('id, full_name, dni, birthdate, phone, email, insurance_provider, medical_notes, professional_name')
            .in('id', patientIds);

          if (patientsError) throw patientsError;

          // 4. Map patients to quick lookup object
          const patientsMap = {};
          patientsData.forEach(p => {
            patientsMap[p.id] = p;
          });

          // 5. Combine data
          const combined = evolutionsData.map(ev => ({
            ...ev,
            patient: patientsMap[ev.patient_id] || { id: ev.patient_id, full_name: 'Paciente Desconocido', dni: '---' }
          }));

          setResults(combined);
        } else {
          setResults([]);
        }
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error(err);
      setError('Error al realizar la búsqueda en la base de datos.');
    } finally {
      setLoading(false);
    }
  };

  // Helper to format date
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return '---';
    }
  };

  // Helper to highlight matching text
  const highlightText = (text, highlight) => {
    if (!highlight.trim()) return text;
    const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === highlight.toLowerCase() 
            ? <mark key={i} className="highlight-mark">{part}</mark> 
            : part
        )}
      </span>
    );
  };

  return (
    <div className="evolution-search-overlay" onClick={onClose}>
      <div className="evolution-search-container glass-modal-premium" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <div className="evolution-search-header">
          <div className="title-wrapper">
            <FileText size={20} className="icon-orange" />
            <h2>Buscador de Evoluciones Clínicas</h2>
          </div>
          <button className="btn-close-p" onClick={onClose}><X size={24} /></button>
        </div>

        {/* Search Input Box */}
        <form onSubmit={handleSearch} className="evolution-search-form">
          <div className="search-input-wrapper">
            <input 
              type="text" 
              placeholder="Buscar palabra clave en las notas médicas (ej: alergia, otitis, control)..." 
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <button type="submit" className="btn-search-trigger" disabled={loading || !query.trim()}>
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
            </button>
          </div>
        </form>

        {/* Results Body */}
        <div className="evolution-search-body">
          {loading && (
            <div className="search-state-msg">
              <Loader2 className="animate-spin" size={36} />
              <p>Escaneando fichas clínicas en la base de datos...</p>
            </div>
          )}

          {!loading && error && (
            <div className="search-state-msg error">
              <p>{error}</p>
            </div>
          )}

          {!loading && !searched && (
            <div className="search-state-msg placeholder">
              <Search size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
              <p>Ingresa un término de búsqueda para comenzar.</p>
              <span>El buscador escaneará todo el contenido textual guardado en las evoluciones médicas.</span>
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div className="search-state-msg placeholder">
              <p>No se encontraron evoluciones que contengan "{query}".</p>
              <span>Intenta con otra palabra clave o verifica la ortografía.</span>
            </div>
          )}

          {!loading && searched && results.length > 0 && (
            <div className="evolution-results-list">
              <p className="results-count">Se encontraron {results.length} coincidencias:</p>
              {results.map((item) => (
                <div key={item.id} className="evolution-result-card">
                  <div className="card-top">
                    <div className="patient-info">
                      <User size={16} className="icon-green" />
                      <span className="patient-name">{item.patient?.full_name?.toUpperCase()}</span>
                      <span className="patient-dni-badge">DNI: {item.patient?.dni || '---'}</span>
                    </div>
                    <button 
                      className="btn-view-patient-card" 
                      onClick={() => {
                        onSelectPatient(item.patient);
                        onClose();
                      }}
                    >
                      VER PACIENTE
                    </button>
                  </div>

                  <div className="card-middle">
                    <p className="evolution-content">
                      {highlightText(item.content, query)}
                    </p>
                  </div>

                  <div className="card-bottom">
                    <div className="meta-info">
                      <Clock size={12} />
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                    <span className="meta-prof">Registró: {item.professional_name || 'Desconocido'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EvolutionSearchModal;
